/**
 * The Orion mark and lockup.
 *
 * Drawn as inline SVG rather than shipped as an image file for two reasons: the
 * gradient has to pick up the brand tokens (so it stays correct if the palette moves),
 * and each instance needs its own gradient id — a shared id would make every logo on
 * the page inherit the first one's fill.
 *
 * The mark is an open ring with a four-point sparkle at the upper right: the ring is
 * the "O" of Orion, the sparkle its star. Clearspace and minimum size follow the
 * guidelines (§02) — the lockup hides its wordmark below 120px rather than letting it
 * render illegibly.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

let instanceCounter = 0;

export function OrionMark({ className, title }: { className?: string; title?: string }) {
  // useId would be ideal but must stay stable across the SSR-less render here; a
  // module counter is sufficient and avoids colliding ids between instances.
  const gradientId = React.useMemo(() => `orion-gradient-${++instanceCounter}`, []);

  return (
    <svg
      viewBox="0 0 50 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('shrink-0', className)}
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <defs>
        <linearGradient id={gradientId} x1="6" y1="42" x2="42" y2="6" gradientUnits="userSpaceOnUse">
          <stop stopColor="var(--brand-blue)" />
          <stop offset="1" stopColor="var(--brand-violet)" />
        </linearGradient>
      </defs>

      {/* Open ring — the gap sits at the upper right, where the sparkle lives. */}
      <path
        d="M32.8 10.4A17 17 0 1 0 40.6 24"
        stroke={`url(#${gradientId})`}
        strokeWidth="5"
        strokeLinecap="round"
      />

      {/*
        Four-point sparkle — the star of Orion, sitting in the ring's gap. Scaled to
        roughly a quarter of the ring so it reads as part of the mark at 24px rather
        than disappearing, matching the lockup in the guidelines.
      */}
      <path
        d="M38.6 2.6c.3-1.1 1.9-1.1 2.2 0l1.35 4.75a1.5 1.5 0 0 0 1.05 1.05l4.75 1.35c1.1.3 1.1 1.9 0 2.2l-4.75 1.35a1.5 1.5 0 0 0-1.05 1.05l-1.35 4.75c-.3 1.1-1.9 1.1-2.2 0l-1.35-4.75a1.5 1.5 0 0 0-1.05-1.05l-4.75-1.35c-1.1-.3-1.1-1.9 0-2.2l4.75-1.35a1.5 1.5 0 0 0 1.05-1.05L38.6 2.6Z"
        fill={`url(#${gradientId})`}
      />
    </svg>
  );
}

/**
 * Full lockup: mark + ORION wordmark + INSURANCE OS descriptor.
 *
 * The wordmark is set in the display face with the wide tracking the guidelines
 * specify; Latin-only text, so Space Grotesk always applies here.
 */
export function OrionLockup({
  className,
  size = 'default',
  tone = 'onDark',
}: {
  className?: string;
  size?: 'sm' | 'default' | 'lg';
  /** Which surface the lockup sits on — decides the wordmark ink. */
  tone?: 'onDark' | 'onLight';
}) {
  const markSize = { sm: 'size-7', default: 'size-8', lg: 'size-11' }[size];
  const wordSize = { sm: 'text-[13px]', default: 'text-[15px]', lg: 'text-xl' }[size];
  const subSize = { sm: 'text-[7px]', default: 'text-[8px]', lg: 'text-[10px]' }[size];

  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <OrionMark className={markSize} title="Orion Insurance OS" />
      <span className="flex min-w-0 flex-col leading-none">
        <span
          className={cn(
            'font-display font-bold tracking-[0.22em]',
            wordSize,
            tone === 'onDark' ? 'text-white' : 'text-[color:var(--brand-ink)]',
          )}
        >
          ORION
        </span>
        <span
          className={cn(
            'font-display mt-1 font-medium tracking-[0.2em]',
            subSize,
            tone === 'onDark' ? 'text-[color:var(--brand-blue)]' : 'text-[color:var(--brand-blue)]',
          )}
        >
          INSURANCE&nbsp;OS
        </span>
      </span>
    </span>
  );
}
